import React from "react";
import { Modal, Button, Form } from "react-bootstrap";
import ModalDialog from "../ModalDialog";
import TextInput from "../TextInput";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import TextAreaInput from "../TextAreaInput";
import Dropdown from "../Dropdown";
import MultiSelectDropdown from "../MultiSelectDropdown";
import EnvironmentThumbnail from "../admin/EnvironmentThumbnail";
import { format, startOfDay } from "date-fns";
import { withNamespaces } from "react-i18next";

const MissionAddEdit = ({
  formData,
  formDataError,
  showModal,
  handleInputChange,
  handleSubmit,
  closeModal,
  headerText,
  submitButtonText,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  statusOptions,
  handleChangeStatus,
  environmentOptions,
  environmentData = [],
  handleChangeEnvironment,
  scenarioOptions,
  handleChangeScenario,
  departmentoptions,
  handleChangeDepartments,
  assignmentTypeOptions,
  handleChangeAssignmentType,
  userOptions = [],
  handleChangeUsers,
  assignmentWarning = "",
  isTemplateMode = false,
  t,
}) => {
  const today = startOfDay(new Date());
  const selectedEnvironment =
    (Array.isArray(environmentData) ? environmentData : []).find(
      (item) => String(item.id) === String(formData?.environment?.value)
    ) || null;
  const assignmentType = formData?.assignmentType?.value;

  const handleStartDateChange = (date) => {
    if (!date) {
      setStartDate(null);
      handleInputChange({
        target: {
          name: "startAt",
          value: "",
        },
      });
      return;
    }

    const normalizedStart = startOfDay(date);
    if (normalizedStart < today) {
      return;
    }

    setStartDate(normalizedStart);
    handleInputChange({
      target: {
        name: "startAt",
        value: format(normalizedStart, "yyyy-MM-dd"),
      },
    });

    if (endDate && startOfDay(endDate) < normalizedStart) {
      setEndDate(normalizedStart);
      handleInputChange({
        target: {
          name: "endAt",
          value: format(normalizedStart, "yyyy-MM-dd"),
        },
      });
    }
  };

  const handleEndDateChange = (date) => {
    if (!date) {
      setEndDate(null);
      handleInputChange({
        target: {
          name: "endAt",
          value: "",
        },
      });
      return;
    }

    const normalizedEnd = startOfDay(date);
    const minEnd = startDate ? startOfDay(startDate) : today;
    if (normalizedEnd < minEnd) {
      alert("End date cannot be before start date.");
      return;
    }

    setEndDate(normalizedEnd);
    handleInputChange({
      target: {
        name: "endAt",
        value: format(normalizedEnd, "yyyy-MM-dd"),
      },
    });
  };

  return (
    <ModalDialog open={showModal} onClose={closeModal} size="lg">
      <ModalDialog.Header closeButton>
        <Modal.Title>{headerText}</Modal.Title>
      </ModalDialog.Header>
      <ModalDialog.Body>
        <Form>
          <div className="form_holder">
            <div className="row">
              <div className="col-md-12">
                <TextInput
                  type="text"
                  name="name"
                  label={`${t("nameColumn")}:*`}
                  value={formData.name}
                  onChange={handleInputChange}
                  error={formDataError.name}
                  placeholder=""
                />
              </div>
              <div className="col-md-6">
                <Dropdown
                  label={`${t("mapColumn")}:*`}
                  value={formData.environment}
                  options={environmentOptions}
                  onChange={handleChangeEnvironment}
                  error={formDataError.environment}
                  placeholder="Select map"
                />
                {selectedEnvironment && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                      marginTop: 12,
                    }}
                  >
                    <EnvironmentThumbnail environment={selectedEnvironment} />
                    <span>{selectedEnvironment.name}</span>
                  </div>
                )}
              </div>
              <div className="col-md-6">
                <Dropdown
                  label={`${t("scenarioColumn")}:*`}
                  value={formData.scenario}
                  options={scenarioOptions}
                  onChange={handleChangeScenario}
                  error={formDataError.scenario}
                  isDisabled={!formData?.environment?.value}
                  placeholder={
                    formData?.environment?.value
                      ? "Select scenario"
                      : "Select map first"
                  }
                />
              </div>
              <div className="col-md-6">
                <Dropdown
                  label="Assign to:*"
                  value={formData.assignmentType}
                  options={assignmentTypeOptions}
                  onChange={handleChangeAssignmentType}
                  error={formDataError.assignmentType}
                  placeholder="Select assignment"
                />
              </div>
              {!isTemplateMode && (
                <div className="col-md-6">
                  <Dropdown
                    label={`${t("statusColumn")}:*`}
                    value={formData.status}
                    options={statusOptions}
                    onChange={handleChangeStatus}
                    error={formDataError.status}
                  />
                </div>
              )}
              {assignmentType === "custom" && (
                <>
                  <div className="col-md-12">
                    <MultiSelectDropdown
                      label={`${t("departmentColumn")}`}
                      options={departmentoptions}
                      value={formData.departments || []}
                      onChange={handleChangeDepartments}
                      error={formDataError.departments}
                      placeholder="Select departments"
                    />
                  </div>
                  <div className="col-md-12">
                    <MultiSelectDropdown
                      label="Users"
                      options={userOptions}
                      value={formData.users || []}
                      onChange={handleChangeUsers}
                      error={formDataError.users}
                      placeholder="Select users"
                    />
                  </div>
                  {assignmentWarning ? (
                    <div className="col-md-12">
                      <div
                        className="invalid-feedback d-block"
                        style={{ marginTop: "-4px", marginBottom: "8px" }}
                      >
                        {assignmentWarning}
                      </div>
                    </div>
                  ) : null}
                </>
              )}
              {!isTemplateMode && (
                <>
                  <div className="col-md-6">
                    <label>{`${t("startDateLabel")}:*`}</label>
                    <DatePicker
                      selected={startDate}
                      onChange={handleStartDateChange}
                      dateFormat="yyyy-MM-dd"
                      minDate={today}
                      className={`form-control ${
                        formDataError.startAt ? "is-invalid" : ""
                      }`}
                    />
                    {formDataError.startAt && (
                      <div className="invalid-feedback d-block">
                        {formDataError.startAt}
                      </div>
                    )}
                  </div>
                  <div className="col-md-6">
                    <label>{`${t("endDateLabel")}:*`}</label>
                    <DatePicker
                      selected={endDate}
                      onChange={handleEndDateChange}
                      dateFormat="yyyy-MM-dd"
                      className={`form-control ${
                        formDataError.endAt ? "is-invalid" : ""
                      }`}
                      minDate={startDate || today}
                    />
                    {formDataError.endAt && (
                      <div className="invalid-feedback d-block">
                        {formDataError.endAt}
                      </div>
                    )}
                  </div>
                </>
              )}
              <div className="col-md-12">
                <TextAreaInput
                  type="text"
                  name="description"
                  label={t("descriptionColumn")}
                  value={formData.description}
                  onChange={handleInputChange}
                  error={formDataError.description}
                  placeholder=""
                />
              </div>
            </div>
          </div>
        </Form>
      </ModalDialog.Body>
      <ModalDialog.Footer>
        <Button variant="secondary" onClick={closeModal}>
          Close
        </Button>
        <Button variant="primary" onClick={handleSubmit}>
          {submitButtonText}
        </Button>
      </ModalDialog.Footer>
    </ModalDialog>
  );
};

export default withNamespaces()(MissionAddEdit);
